import dayjs, { Dayjs } from "utils/dayjs"

import { ApiLinkEntity, ApiLinkRel, ArticleType, Currency, DurationType, Purpose } from "config/constants"
import { Guid, IApiLink, ITranslations } from "@/types"
import CustomerExtended, { ICustomerExtendedDto } from "./CustomerExtended"
import Customer, { ICustomerDto } from "./Customer"
import { toDateWithoutTimezone } from "utils/utils"
import getCustomerBasedCurrency from "utils/getCustomerBasedCurrency"
import AssetArticle from "./AssetArticle"
import i18n from "intl/i18n"
import OfferInvoicePosition, { IGetOfferInvoicePositionDTO, IOfferInvoicePosition } from "./OfferInvoicePosition"
import CalculationAdjustment, { IGetCalculationAdjustmentDto, IPostCalculationAdjustmentDto } from "./CalculationAdjustment"
import AssetDisplayStatus from "./AssetDisplayStatus"

export type OfferPurpose = Purpose.sale | Purpose.rent

export enum OfferStatus {
    draft = "draft",
    offered = "offered",
    accepted = "accepted",
    rejected = "rejected",
}

export enum OfferSortBy {
    startDate = "startDate",
    article = "article",
    offerNo = "offerNo",
    purpose = "purpose",
    customer = "customer",
}

export interface IOfferItem {
    articleId: Guid
    assetIds: (Guid | null)[]
}

export interface IOfferPendingArticle {
    id: Guid
    articleType: ArticleType
    description: string
    descriptionTranslations: ITranslations
    number: string
    features?: ITranslations
}

export interface IOfferPendingItem {
    article: IOfferPendingArticle
    assets: {
        assetId: Guid | null
        asset: AssetArticle | null
        stateOfStorage: AssetDisplayStatus | null
    }[]
}

export type OfferConfiguration = IOfferItem[]
export type OfferPendingConfiguration = IOfferPendingItem[]

interface IGetOfferBaseDto {
    id: Guid
    articles: OfferConfiguration
    customer: ICustomerExtendedDto
    currency?: Currency | null
    number: string
    time: {
        start: string
        duration?: number | null
        durationType?: DurationType | null
    }
    purpose: OfferPurpose
    status: OfferStatus
    created: string
    createdBy: Guid
    changed: string
    changedBy: Guid
    links: IApiLink[]
}

class OfferBase {
    id: Guid
    articles: OfferConfiguration
    customer: CustomerExtended
    currency?: Currency
    number: string
    time: {
        start: Dayjs
        duration?: number
        durationType?: DurationType
    }
    purpose: OfferPurpose
    status: OfferStatus
    created: Dayjs
    createdBy: Guid
    changed: Dayjs
    changedBy: Guid
    linkUpdate?: IApiLink
    linkDelete?: IApiLink
    linkUpdateStatus?: IApiLink
    linkGetOfferDocument?: IApiLink

    constructor(dto: IGetOfferBaseDto) {
        this.id = dto.id
        this.articles = dto.articles
        this.customer = new CustomerExtended(dto.customer)
        this.currency = dto.currency ?? undefined
        this.number = dto.number
        this.time = {
            start: dayjs(dto.time.start),
            duration: typeof dto.time.duration === "number" ? dto.time.duration : undefined,
            durationType: dto.time.durationType ?? undefined,
        }
        this.purpose = dto.purpose
        this.status = dto.status
        this.created = dayjs(dto.created)
        this.createdBy = dto.createdBy
        this.changed = dayjs(dto.changed)
        this.changedBy = dto.changedBy
        this.linkUpdate = dto.links.find(link => link.entity === ApiLinkEntity.offer && link.rel === ApiLinkRel.update)
        this.linkDelete = dto.links.find(link => link.entity === ApiLinkEntity.offer && link.rel === ApiLinkRel.delete)
        this.linkUpdateStatus = dto.links.find(link => link.entity === ApiLinkEntity.offerStatus && link.rel === ApiLinkRel.update)
        this.linkGetOfferDocument = dto.links.find(link => link.entity === ApiLinkEntity.offerDocument && link.rel === ApiLinkRel.self)
    }

    /**
     * The effective offer currency: the persisted value, falling back to the customer-based derivation for
     * responses without the field.
     */
    get requestCurrency(): Currency | null {
        return this.currency ?? getCustomerBasedCurrency({
            hasCustomer: !!this.customer?.name,
            customerCurrency: this.customer?.currency,
        })
    }
}


export interface IGetOfferDto extends IGetOfferBaseDto {
    configurationCompleted: boolean
    language: string
    additionalDetails?: string | null
    customerEnquiryDate?: string | null
    customerEnquiryNumber?: string | null
    salesPersonNumber?: string | null
    salesAdminNumber?: string | null
    warehouseId?: Guid | null
    profitCenterId?: Guid | null
    termsOfPaymentCode?: string | null
    deliveryTermId?: Guid | null
    invoicePositions?: IGetOfferInvoicePositionDTO[]
    adjustments?: IGetCalculationAdjustmentDto[]
    headerText?: string | null
    footerText?: string | null
    links: IApiLink[]
    eTag?: string
}

interface ICreateOfferDto {
    articles: OfferConfiguration
    configurationCompleted: boolean
    customer: ICustomerDto
    currency: Currency | null
    language: string
    time: {
        start: string
        duration: number | null
        durationType: DurationType | null
    }
    purpose: OfferPurpose
    additionalDetails: string | null
    customerEnquiryDate: string | null
    customerEnquiryNumber: string | null
    salesPersonNumber: string | null
    salesAdminNumber: string | null
    warehouseId: Guid | null
    profitCenterId: Guid | null
    termsOfPaymentCode: string | null
    deliveryTermId: Guid | null
    headerText: string | null
    footerText: string | null
    invoicePositions?: IOfferInvoicePosition[] | null
    adjustments?: IPostCalculationAdjustmentDto[] | null
}

export interface ICreateOffer {
    articles: OfferConfiguration
    configurationCompleted: boolean
    customer: Customer
    currency: Currency | null
    language: string
    time: {
        start: Dayjs
        duration: number | null
        durationType: DurationType | null
    }
    purpose: OfferPurpose
    additionalDetails: string | null
    customerEnquiryDate: Dayjs | null
    customerEnquiryNumber: string | null
    salesPersonNumber: string | null
    salesAdminNumber: string | null
    warehouseId: Guid | null
    profitCenterId: Guid | null
    termsOfPaymentCode: string | null
    deliveryTermId: Guid | null
    headerText: string | null
    footerText: string | null
    invoicePositions?: OfferInvoicePosition[] | null
    adjustments?: CalculationAdjustment[] | null
}

interface IUpdateOfferDto {
    articles: OfferConfiguration
    configurationCompleted: boolean
    customer: ICustomerDto
    currency: Currency | null
    language: string
    time: {
        start: string
        duration: number | null
        durationType: DurationType | null
    }
    additionalDetails: string | null
    customerEnquiryDate: string | null
    customerEnquiryNumber: string | null
    salesPersonNumber: string | null
    salesAdminNumber: string | null
    warehouseId: Guid | null
    profitCenterId: Guid | null
    termsOfPaymentCode: string | null
    deliveryTermId: Guid | null
    headerText: string | null
    footerText: string | null
    invoicePositions?: IOfferInvoicePosition[] | null
    adjustments?: IPostCalculationAdjustmentDto[] | null
}

export interface IUpdateOffer {
    articles: OfferConfiguration
    configurationCompleted: boolean
    customer: Customer
    currency: Currency | null
    language: string
    time: {
        start: Dayjs
        duration: number | null
        durationType: DurationType | null
    }
    additionalDetails: string | null
    customerEnquiryDate: Dayjs | null
    customerEnquiryNumber: string | null
    salesPersonNumber: string | null
    salesAdminNumber: string | null
    warehouseId: Guid | null
    profitCenterId: Guid | null
    termsOfPaymentCode: string | null
    deliveryTermId: Guid | null
    headerText: string | null
    footerText: string | null
    invoicePositions?: OfferInvoicePosition[] | null
    adjustments?: CalculationAdjustment[] | null
}


export class Offer extends OfferBase {
    configurationCompleted: boolean
    language: string
    additionalDetails?: string
    customerEnquiryDate?: Dayjs
    customerEnquiryNumber?: string
    salesPersonNumber?: string
    salesAdminNumber?: string
    warehouseId?: Guid
    profitCenterId?: Guid
    termsOfPaymentCode?: string
    deliveryTermId?: Guid
    headerText?: string
    footerText?: string
    invoicePositions: OfferInvoicePosition[]
    adjustments: CalculationAdjustment[]

    eTag?: string

    constructor(dto: IGetOfferDto) {
        super(dto)
        this.configurationCompleted = dto.configurationCompleted
        this.language = dto.language
        this.additionalDetails = dto.additionalDetails ?? undefined
        this.customerEnquiryDate = dto.customerEnquiryDate ? dayjs(dto.customerEnquiryDate) : undefined
        this.customerEnquiryNumber = dto.customerEnquiryNumber ?? undefined
        this.salesPersonNumber = dto.salesPersonNumber ?? undefined
        this.salesAdminNumber = dto.salesAdminNumber ?? undefined
        this.warehouseId = dto.warehouseId ?? undefined
        this.profitCenterId = dto.profitCenterId ?? undefined
        this.termsOfPaymentCode = dto.termsOfPaymentCode ?? undefined
        this.deliveryTermId = dto.deliveryTermId ?? undefined
        this.headerText = dto.headerText ?? undefined
        this.footerText = dto.footerText ?? undefined
        this.invoicePositions = dto.invoicePositions?.map(ip => new OfferInvoicePosition(ip, { purpose: this.purpose })) ?? []
        this.adjustments = dto.adjustments?.map((cAdjDto, index) => new CalculationAdjustment({ ...cAdjDto, id: `custom_id_${index}` })) ?? []
        this.eTag = dto.eTag
    }

    static toCreateDto(offer: ICreateOffer): ICreateOfferDto {
        return {
            articles: offer.articles.map(item => {
                return {
                    ...item,
                    assetIds: item.assetIds.filter(a => !!a),
                }
            }),
            configurationCompleted: offer.configurationCompleted,
            customer: offer.customer.toDto()!,
            currency: offer.currency,
            language: offer.language,
            time: {
                start: toDateWithoutTimezone(offer.time.start),
                duration: offer.time.duration,
                durationType: offer.time.durationType,
            },
            purpose: offer.purpose,
            additionalDetails: offer.additionalDetails,
            customerEnquiryDate: offer.customerEnquiryDate ? toDateWithoutTimezone(offer.customerEnquiryDate) : null,
            customerEnquiryNumber: offer.customerEnquiryNumber,
            salesPersonNumber: offer.salesPersonNumber,
            salesAdminNumber: offer.salesAdminNumber,
            warehouseId: offer.warehouseId,
            profitCenterId: offer.profitCenterId,
            termsOfPaymentCode: offer.termsOfPaymentCode,
            deliveryTermId: offer.deliveryTermId,
            headerText: offer.headerText,
            footerText: offer.footerText,
            invoicePositions: offer.invoicePositions?.map(ip => ip.toDto()) ?? null,
            adjustments: offer.adjustments?.map(cAdj => cAdj.toPostDto()) ?? null,
        }
    }

    static toUpdateDto(offer: IUpdateOffer): IUpdateOfferDto {
        return {
            articles: offer.articles.map(item => {
                return {
                    ...item,
                    assetIds: item.assetIds.filter(a => !!a),
                }
            }),
            configurationCompleted: offer.configurationCompleted,
            customer: offer.customer.toDto()!,
            currency: offer.currency,
            language: offer.language,
            time: {
                start: toDateWithoutTimezone(offer.time.start),
                duration: offer.time.duration,
                durationType: offer.time.durationType,
            },
            additionalDetails: offer.additionalDetails,
            customerEnquiryDate: offer.customerEnquiryDate ? toDateWithoutTimezone(offer.customerEnquiryDate) : null,
            customerEnquiryNumber: offer.customerEnquiryNumber,
            salesPersonNumber: offer.salesPersonNumber,
            salesAdminNumber: offer.salesAdminNumber,
            warehouseId: offer.warehouseId,
            profitCenterId: offer.profitCenterId,
            termsOfPaymentCode: offer.termsOfPaymentCode,
            deliveryTermId: offer.deliveryTermId,
            headerText: offer.headerText,
            footerText: offer.footerText,
            invoicePositions: offer.invoicePositions?.map(ip => ip.toDto()) ?? null,
            adjustments: offer.adjustments?.map(ip => ip.toPostDto()) ?? null,
        }
    }
}



export interface IGetOfferItemDto extends IGetOfferBaseDto {

}

export class OfferItem extends OfferBase {
    get mainArticleId(): Guid | undefined {
        return this.articles[0]
            ? this.articles[0].articleId
            : undefined
    }

    get mainAssetId(): Guid | undefined {
        return this.articles[0]?.assetIds[0] ?? undefined
    }

    get assetCount(): number {
        return this.articles.reduce((acc, item) => acc + item.assetIds.length, 0)
    }
}


export function getOfferStatusLabel(status: OfferStatus): string {
    return {
        [OfferStatus.draft]: i18n.t('offer.status.draft'),
        [OfferStatus.offered]: i18n.t('offer.status.offered'),
        [OfferStatus.accepted]: i18n.t('offer.status.accepted'),
        [OfferStatus.rejected]: i18n.t('offer.status.rejected'),
    }[status] || status
}


export function getOfferStatusColorClass(status: OfferStatus): string {
    return {
        [OfferStatus.draft]: 'bg-brand-yellow',
        [OfferStatus.offered]: 'bg-grey-400',
        [OfferStatus.accepted]: 'bg-green-400',
        [OfferStatus.rejected]: 'bg-red-400',
    }[status] || 'bg-black'
}

export function formatOfferNumber(number: string = "") {
    const preparedNumber = number?.trim().replaceAll("-", "")
    if (!preparedNumber) return ""
    if (preparedNumber.length < 2) return preparedNumber
    return preparedNumber.slice(0, 1) + "-" + preparedNumber.slice(1, 3) + "-" + preparedNumber.slice(3)
}
